import { SyntheticEvent, useCallback, useEffect, useRef, useState } from "react";
import ColumnsTable from "./components/ColumnsTable";
import { useSearchParams } from "react-router-dom";
import {
  getAllRidesApi,
  getCurrentRidesApi,
  getRidesByFilterApi,
  searchRidesApi,
} from "../../../services/customAPI";
import {
  Ride,
  RideFilterEnum,
  RideListData,
  RideTableRowDTO,
  PageClickEvent,
} from "types/api.types";
import ReactPaginate from "react-paginate";
import "./rides.css";
import Loader from "components/loader/loader";
import Navbar from "components/navbar";
import Card from "components/card";
import { t } from "i18next";
import { getStatusText } from "utils/commons";
import { statusOptions } from "types/types";
import { Formatters } from "shared-lib";

const Rides = () => {
  const [searchParams, setSearchParams] = useSearchParams();


  // Read filter from URL, defaulting to ALL
  const getFilterFromUrl = (): RideFilterEnum => {
    const data = searchParams.get("status") || searchParams.get("data");
    if (data === RideFilterEnum.COMPLETED || data === "completed") return RideFilterEnum.COMPLETED;
    if (data === RideFilterEnum.CURRENT_RIDES || data === "ongoing-rides") return RideFilterEnum.CURRENT_RIDES;
    if (data === RideFilterEnum.CANCELLED || data === "cancelled") return RideFilterEnum.CANCELLED;
    if (data === RideFilterEnum.HAS_DISPUTE || data === "has-dispute") return RideFilterEnum.HAS_DISPUTE;
    return RideFilterEnum.ALL;
  };

  // Read page from URL, defaulting to 1
  const getPageFromUrl = (): number => {
    const page = searchParams.get("page");
    const parsed = page ? parseInt(page, 10) : 1;
    return Number.isNaN(parsed) ? 1 : Math.max(1, parsed);
  };

  const [allRideData, setAllRideData] = useState<RideTableRowDTO[]>([]);
  const [pageCount, setPageCount] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [rideStatus, setRideStatus] = useState<RideFilterEnum>(getFilterFromUrl());
  const [currentPage, setCurrentPage] = useState(getPageFromUrl());
  const [searchText, setSearchText] = useState("");

  // Sync URL params whenever filter or page changes
  const updateUrlParams = useCallback((status: RideFilterEnum, page: number) => {
    const params = new URLSearchParams();
    if (status !== RideFilterEnum.ALL) params.set("status", status);
    if (page > 1) params.set("page", String(page));
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const convertToUsableRideArray = useCallback(
    (rideArray: Ride[]): RideTableRowDTO[] => {
      return rideArray.map((ride) => {
        const createdAt = ride.createdAt ?? new Date();
        const dateTime = new Date(createdAt);
        return {
          bookingDate: Formatters.formatDate(dateTime),
          bookingTime: Formatters.formatTime(dateTime),
          riderMobileNum: ride.rider?.mobileNumber
            ? Formatters.formatPhoneNumber(ride.rider.mobileNumber)
            : "N/A",
          DriverMobileNum: ride.driver?.mobileNumber
            ? Formatters.formatPhoneNumber(ride.driver.mobileNumber)
            : "N/A",
          fare: ride.fare,
          platform: ride.platform,
          status: getStatusText(undefined, ride.status),
          origin: ride.pickUpAddress,
          destination: ride.dropAddress,
          view: ride.id,
          driverId: ride.driver?.id ?? null,
          riderId: ride.rider?.id ?? null,
        };
      });
    },
    []
  );

  const fetchRides = useCallback(async (status: RideFilterEnum, page: number): Promise<void> => {
    try {
      setLoading(true);
      setErrorMessage("");
      const response =
        status === RideFilterEnum.ALL
          ? await getAllRidesApi({ page, limit })
          : status === RideFilterEnum.CURRENT_RIDES
            ? await getCurrentRidesApi({ page, limit })
            : await getRidesByFilterApi({ page, limit, filter: status });

      if (response && response.data) {
        const rideData = response.data;
        setPageCount(Math.ceil(rideData.totalCount / limit));
        setTotalCount(rideData.totalCount);
        setAllRideData(convertToUsableRideArray(rideData.rides || []));
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Unknown error";
      console.error("fetchRides error:", msg);
      setErrorMessage(t("Erro ao carregar corridas"));
    } finally {
      setLoading(false);
    }
  }, [convertToUsableRideArray, limit]);

  const searchRidesFn = useCallback(async (page: number): Promise<void> => {
    try {
      setLoading(true);
      setErrorMessage("");
      const response = await searchRidesApi({
        page,
        limit,
        query: searchText.trim(),
      });
      if (response?.data) {
        const rideData = response.data;
        setPageCount(Math.ceil(rideData.totalCount / limit));
        setTotalCount(rideData.totalCount);
        setAllRideData(convertToUsableRideArray(rideData.rides));
      }
    } catch (error) {
      console.error(error);
      setAllRideData([]);
      setPageCount(1);
      setErrorMessage(t("Erro ao buscar corridas"));
    } finally {
      setLoading(false);
    }
  }, [convertToUsableRideArray, limit, searchText]);

  const handleSearchSubmit = useCallback(
    (event: SyntheticEvent<EventTarget>): void => {
      event.preventDefault();
      setCurrentPage(1);
      searchRidesFn(1);
    },
    [searchRidesFn]
  );

  const handlePageClick = useCallback(
    async (event: PageClickEvent): Promise<void> => {
      const newPage = event.selected + 1;
      setCurrentPage(newPage);
      updateUrlParams(rideStatus, newPage);
      if (searchText !== "") {
        await searchRidesFn(newPage);
      } else {
        await fetchRides(rideStatus, newPage);
      }
    },
    [fetchRides, rideStatus, searchRidesFn, searchText, updateUrlParams]
  );

  const handleStatusChange = useCallback(
    (status: RideFilterEnum) => {
      setRideStatus(status);
      setCurrentPage(1);
      updateUrlParams(status, 1);
    },
    [updateUrlParams]
  );

  // Fetch data when status or page changes
  useEffect(() => {
    if (searchText.trim() === "") {
      fetchRides(rideStatus, currentPage);
    }
  }, [rideStatus, currentPage, fetchRides, searchText]);

  // Compute display range
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * limit + 1;
  const endItem = Math.min(currentPage * limit, totalCount);

  return (
    <div>
      <Navbar
        flag={true}
        brandText={t("Rides")}
        handleSearch={handleSearchSubmit}
        setSearchText={setSearchText}
      />
      {errorMessage ? (
        <Card extra="w-full mt-4 p-4 h-full">
          <div className="text-sm text-red-400">{errorMessage}</div>
        </Card>
      ) : null}
      {loading ? (
        <Loader />
      ) : (
        <div className="mt-4">
          <ColumnsTable
            tableData={allRideData}
            statusOptions={statusOptions}
            setRideStatus={handleStatusChange}
            rideStatus={rideStatus}
          />

          <div
            className="mx-2 mt-2"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              color: "#5E6278",
            }}
          >
            <h5>
              {startItem.toLocaleString("en-US", { minimumIntegerDigits: 2 })} -{" "}
              {endItem.toLocaleString("en-US", { minimumIntegerDigits: 2 })}
              {" "}de {totalCount}
            </h5>
            <div style={{ marginTop: "1rem" }}>
              <ReactPaginate
                breakLabel=" .  .  . "
                nextLabel=">"
                onPageChange={handlePageClick}
                pageRangeDisplayed={3}
                pageCount={pageCount}
                previousLabel="<"
                renderOnZeroPageCount={null}
                marginPagesDisplayed={2}
                containerClassName="pagination"
                pageClassName="page-item"
                pageLinkClassName="page-link"
                previousClassName="previous-page-btn"
                previousLinkClassName="page-link"
                nextClassName="next-page-btn"
                nextLinkClassName="page-link"
                activeClassName="active"
                forcePage={currentPage - 1}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rides;
